/***
*
*   APP LAYOUT
*   Main application layout containing the navigation
*   and header (title, secondary nav and signed in user)
*
*   PROPS
*   title: title of the view
*
**********/

import React, { Fragment, useContext, useState, useEffect } from 'react';
import { AuthContext, useAPI, AppNav, Header } from 'components/lib';
import ClassNames from 'classnames';
import Style from './app.module.scss';
import '../layout.scss'; // globals
import IdleTimeOutHandler from '../idleTImeoutHandler';

export function AppLayout(props) {
  const context = useContext(AuthContext);
  
  // Initialize sticky state from localStorage
  const [isMenuSticky, setIsMenuSticky] = useState(() => {
    try {
      const stored = localStorage.getItem('ledgerview-menu-sticky');
      return stored === 'true';
    } catch (error) {
      return false;
    }
  });
  
  const css = ClassNames(Style.app, 'with-sidebar', { 'sticky-menu': isMenuSticky });
  const connectionsAPI = useAPI(context.user ? '/api/app_connection_names' : null);

  const handleStickyChange = (sticky) => {
    setIsMenuSticky(sticky);
  };

  useEffect(() => {
    if (connectionsAPI?.data?.length) {
      context.updateOrgs(connectionsAPI.data); // Update orgs in the AuthContext
    }
  }, [connectionsAPI.data, context]);

  // Filter navigation items based on account type
  const allItems = [
    { label: 'Home', icon: 'home', link: '/home' },
    { label: 'Dashboard', icon: 'layout', link: '/dashboard' }, // changed from 'flag' to 'layout'
    { label: 'Health Score', icon: 'activity', link: '/healthScore', healthScoreOnly: true },
    { label: 'Work', icon: 'briefcase',
      submenu: [
        { label: 'Clean-Up', icon: 'check-square', link: '/client/work/cleanup' }, // changed from 'briefcase' to 'check-square'
        // { label: 'GL Review', icon: 'book-open', link: '/app/glMonitors' }, // changed from 'briefcase' to 'book-open'
        { label: 'Reports', icon: 'file-text', link: '/client/work/reports' },
      ],
      requiresFullMonitoring: true
    },
    { label: 'Monitors', icon: 'bell', link: '/client/monitors/rules', requiresFullMonitoring: true },
    { label: 'Activity Logs', icon: 'clipboard', link: '/client/logs', requiresFullMonitoring: true }, // changed from 'list' to 'clipboard'
    { label: 'Connections', icon: 'link', link: '/client/connections' },
  ];

  // Filter items based on account type from context
  const items = context.user?.account_type === 'health_score_only' 
    ? allItems.filter(item => !item.requiresFullMonitoring)
    : allItems;

  const onLogout = () => {
    context.signout();
  };

  return (
    <Fragment>
      <IdleTimeOutHandler
        onActive={() => {
          // Handler for when user becomes active
        }}
        onIdle={() => {
          onLogout();
        }}
      />

      <AppNav
        type="popup"
        items={items}
        onStickyChange={handleStickyChange}
        bottomItems={[
          ...(context.user.permission !== 'user'
            ? [{ label: 'Settings', icon: 'settings', link: '/app/account' }]
            : []),
          // { label: 'Profile', icon: 'user', link: '/profile/index' },
          { label: 'Support', icon: 'help-circle', link: '/help' },
          { label: 'Sign Out', icon: 'log-out', action: context.signout },
        ]}
      />

      <main className={css}>
        <Header title={props.title} />
        {<props.children {...props.data} />}
      </main>
    </Fragment>
  );
}
